import { Search } from 'lucide-react';
import { CSSProperties, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from '../../Shared/Input/Input';
import { BrandGlyph } from '../ActionBox/BrandGlyph';
import {
  MCP_CATALOG,
  MCP_CATALOG_CATEGORY_ORDER,
  MCP_CATALOG_POPULAR,
  McpCatalogCategory,
  McpCatalogEntry,
} from './mcpCatalog';
import { PostHogEventEnum, type RemoteMcpEntryPoint } from '@/Domain/enum/PostHogEvent.enum';
import { normalizeCatalogQuery } from '@/Domain/hooks/Action/RemoteMcp/remoteMcpAnalytics';
import { usePostHogAnalytics } from '@/Domain/hooks/App/usePostHog/usePostHogAnalytics.hook';

type Props = {
  onSelect: (entry: McpCatalogEntry, entryPoint: RemoteMcpEntryPoint) => void;
  disabled?: boolean;
};

const CATEGORY_LABEL_KEY: Record<McpCatalogCategory, string> = {
  Popular: 'catalog.category.popular',
  Support: 'catalog.category.support',
  CRM: 'catalog.category.crm',
  Scheduling: 'catalog.category.scheduling',
  'E-commerce': 'catalog.category.ecommerce',
  Payments: 'catalog.category.payments',
  Marketing: 'catalog.category.marketing',
  Communication: 'catalog.category.communication',
  Productivity: 'catalog.category.productivity',
  Data: 'catalog.category.data',
  Search: 'catalog.category.search',
  Travel: 'catalog.category.travel',
  Design: 'catalog.category.design',
  Developer: 'catalog.category.developer',
};

const Tile = ({
  entry,
  disabled,
  onSelect,
  entryPoint,
  idPrefix = 'remote-mcp-catalog-',
}: {
  entry: McpCatalogEntry;
  disabled?: boolean;
  onSelect: (entry: McpCatalogEntry, entryPoint: RemoteMcpEntryPoint) => void;
  entryPoint: RemoteMcpEntryPoint;
  idPrefix?: string;
}) => (
  <button
    type="button"
    disabled={disabled}
    onClick={() => onSelect(entry, entryPoint)}
    data-testid={`${idPrefix}${entry.name}`}
    className="flex cursor-pointer items-center gap-3 rounded-xl border border-border bg-card p-3 text-left transition hover:border-foreground-subtle hover:bg-accent disabled:pointer-events-none disabled:opacity-50"
  >
    <span className="flex size-9 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-muted text-foreground">
      <BrandGlyph icon={entry.icon} name={entry.name} size={20} />
    </span>
    <span className="min-w-0 flex-1">
      <span className="block truncate text-sm font-medium text-foreground">{entry.name}</span>
      <span className="block truncate text-xs text-muted-foreground">{entry.description}</span>
    </span>
  </button>
);

/** A searchable, category-grouped grid of remote MCP servers. Selecting a tile hands the entry
 *  back to the parent, which fills the form and kicks off the connect probe. */
export const McpCatalogPicker = ({ onSelect, disabled }: Props) => {
  const { t } = useTranslation('remote_mcp');
  const { capture } = usePostHogAnalytics();
  const [query, setQuery] = useState('');
  const q = query.trim().toLowerCase();
  const matches = q
    ? MCP_CATALOG.filter(
        (entry) =>
          entry.name.toLowerCase().includes(q) ||
          entry.description.toLowerCase().includes(q) ||
          entry.category.toLowerCase().includes(q),
      )
    : null;

  // Which servers people look for that the catalog doesn't carry. Debounced and de-duped so a
  // word typed past its last match reports the finished query once, not once per keystroke.
  const reportedQueriesRef = useRef<Set<string>>(new Set());
  const noMatches = !!matches && matches.length === 0;
  useEffect(() => {
    const normalized = normalizeCatalogQuery(q);
    if (!noMatches || normalized.length < 2 || reportedQueriesRef.current.has(normalized)) return;
    const timer = setTimeout(() => {
      reportedQueriesRef.current.add(normalized);
      capture(PostHogEventEnum.RemoteMcpCatalogSearchEmpty, { query: normalized });
    }, 800);
    return () => clearTimeout(timer);
  }, [q, noMatches, capture]);

  // Fade the scroll edges only where content is actually clipped, so a hard-sliced row never
  // reads as broken and short/searched lists get no spurious fade.
  const scrollRef = useRef<HTMLDivElement>(null);
  const [edges, setEdges] = useState({ top: false, bottom: false });
  const syncEdges = () => {
    const el = scrollRef.current;
    if (!el) return;
    setEdges({
      top: el.scrollTop > 4,
      bottom: el.scrollTop + el.clientHeight < el.scrollHeight - 4,
    });
  };
  useEffect(syncEdges, [q]);

  const stops = [
    edges.top ? 'transparent 0' : 'black 0',
    edges.top ? 'black 1.5rem' : null,
    edges.bottom ? 'black calc(100% - 1.5rem)' : null,
    edges.bottom ? 'transparent 100%' : 'black 100%',
  ].filter(Boolean);
  const maskImage = `linear-gradient(to bottom, ${stops.join(', ')})`;
  const maskStyle: CSSProperties = { maskImage, WebkitMaskImage: maskImage };

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3">
      <Input
        startIcon={<Search className="size-4 text-foreground-subtle" />}
        placeholder={t('catalog.search_placeholder', { count: MCP_CATALOG.length })}
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        data-testid="remote-mcp-catalog-search"
      />
      {noMatches ? (
        <p className="rounded-xl border border-dashed border-border px-4 py-8 text-center text-sm text-muted-foreground">
          {t('catalog.no_matches')}
        </p>
      ) : (
        <div
          ref={scrollRef}
          onScroll={syncEdges}
          style={maskStyle}
          className="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto pr-1"
        >
          {matches ? (
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {matches.map((entry) => (
                <Tile
                  key={entry.name}
                  entry={entry}
                  disabled={disabled}
                  onSelect={onSelect}
                  entryPoint="catalog_search"
                />
              ))}
            </div>
          ) : (
            MCP_CATALOG_CATEGORY_ORDER.map((category) => {
              const items =
                category === 'Popular'
                  ? MCP_CATALOG_POPULAR
                  : MCP_CATALOG.filter((entry) => entry.category === category);
              if (items.length === 0) return null;
              return (
                <section key={category} className="flex flex-col gap-2">
                  <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    {t(CATEGORY_LABEL_KEY[category])}
                  </h4>
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                    {items.map((entry) => (
                      <Tile
                        key={entry.name}
                        entry={entry}
                        disabled={disabled}
                        onSelect={onSelect}
                        entryPoint={category === 'Popular' ? 'catalog_popular' : 'catalog'}
                        idPrefix={
                          category === 'Popular' ? 'remote-mcp-catalog-popular-' : undefined
                        }
                      />
                    ))}
                  </div>
                </section>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};
